/**
 * LearnArticlePage — premium editorial wellness publication renderer.
 *
 * Replaces the previous plain-markdown render with:
 *   * Premium hero image (Cloudinary, 16:9, fade-in)
 *   * Editorial title + byline + reading time + last updated
 *   * Sticky reading-progress bar
 *   * Auto-generated Table of Contents (H2 anchors)
 *   * Rich editorial markdown blocks (pull-quotes, wellness callouts,
 *     premium ordered/unordered lists, anchor headings, dividers)
 *   * Auto-inserted inline visuals (social 1:1 + pinterest 2:3) at
 *     natural article-section boundaries
 *   * Premium internal-link cards (auto-derived from related[] +
 *     phasec_internal_links)
 *   * FAQ accordion with FAQPage JSON-LD schema
 *   * Pinterest "Save" hover button on hero (deep-link → pin creator)
 *   * Pre-built share intents for Pinterest / X / LinkedIn / Facebook /
 *     WhatsApp ready for the Phase C-9 distribution pipeline
 *   * Full Article + Breadcrumb + FAQPage schema (was already shipped;
 *     extended with author / publisher / image / wordCount)
 */
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import {
  ArrowLeft, ArrowRight, BookOpen, ShieldCheck, Clock, Sparkles,
  ChevronRight,
} from "lucide-react";
import useSeo, { PROD_DOMAIN } from "../lib/seo";
import EditorialMarkdownRenderer, {
  InternalLinkCard, ReadingProgress, slugify,
} from "../components/learn/EditorialMarkdownRenderer";
import ArticleShareRow from "../components/learn/ArticleShareRow";
import ArticleAiEnhancements from "../components/learn/ArticleAiEnhancements";


function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch { return ""; }
}

function readingTimeOf(text, fallback) {
  if (fallback) return fallback;
  const words = (text || "").split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
}


export default function LearnArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // OG / Twitter / Article + FAQPage + Breadcrumb schema
  const heroImage = article?.hero_image_url
    || article?.visual_assets?.find((v) => v.variant === "hero")?.url
    || `${PROD_DOMAIN}/og-default.jpg`;

  useSeo(
    article
      ? {
          title: `${article.title} | Feel Greats Wellness Library`,
          description: article.meta_description,
          canonical: `${PROD_DOMAIN}/learn/${article.slug}`,
          ogType: "article",
          ogImage: heroImage,
          jsonLd: [
            {
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": article.title,
              "description": article.meta_description,
              "url": `${PROD_DOMAIN}/learn/${article.slug}`,
              "image": heroImage,
              "datePublished": article.created_at,
              "dateModified": article.updated_at || article.created_at,
              "author": { "@type": "Organization", "name": "Feel Greats", "url": PROD_DOMAIN },
              "publisher": {
                "@type": "Organization",
                "name": "Feel Greats",
                "logo": { "@type": "ImageObject", "url": `${PROD_DOMAIN}/og-default.jpg` },
              },
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": `${PROD_DOMAIN}/learn/${article.slug}`,
              },
              ...(Array.isArray(article.tags) && article.tags.length
                ? { "keywords": article.tags.join(", ") }
                : {}),
              ...(article.estimated_read_minutes
                ? { "timeRequired": `PT${article.estimated_read_minutes}M` }
                : {}),
              ...(article.body_md
                ? { "wordCount": String(article.body_md.split(/\s+/).filter(Boolean).length) }
                : {}),
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": `${PROD_DOMAIN}/` },
                { "@type": "ListItem", "position": 2, "name": "Learn", "item": `${PROD_DOMAIN}/learn` },
                { "@type": "ListItem", "position": 3, "name": article.title, "item": `${PROD_DOMAIN}/learn/${article.slug}` },
              ],
            },
            ...(Array.isArray(article.faq) && article.faq.length > 0
              ? [{
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  "mainEntity": article.faq
                    .map((f) => {
                      const q = f?.q || f?.question;
                      const a = f?.a || f?.answer;
                      return q && a ? { q, a } : null;
                    })
                    .filter(Boolean)
                    .map(({ q, a }) => ({
                      "@type": "Question",
                      "name": String(q).slice(0, 300),
                      "acceptedAnswer": { "@type": "Answer", "text": String(a).slice(0, 1200) },
                    })),
                }]
              : []),
          ],
        }
      : { title: "Loading… | Feel Greats", description: "" }
  );

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const { data } = await api.get(`/learn/${slug}`);
        setArticle(data);
        // Fire a server-side article_view engagement event so the admin
        // Acquisition Snapshot can show top-performing + top-converting
        // article metrics. Fire-and-forget; never blocks the page.
        try {
          api.post("/engagement", {
            event_type: "article_view",
            meta: { slug, title: data?.title || slug },
          }).catch(() => { /* silent */ });
        } catch { /* ignore */ }
      } catch (e) {
        if (e.response?.status === 404) setNotFound(true);
        setArticle(null);
      } finally {
        setLoading(false);
      }
    })();
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  // Reading-progress bar effect
  useEffect(() => {
    const onScroll = () => {
      const el = document.getElementById("reading-progress-bar");
      if (!el) return;
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      const pct = docH > 0 ? Math.min(1, Math.max(0, window.scrollY / docH)) : 0;
      el.style.transform = `scaleX(${pct})`;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [article]);

  if (loading) {
    return <main className="min-h-[60vh] flex items-center justify-center text-emerald-700" data-testid="learn-article-loading">Loading…</main>;
  }
  if (notFound || !article) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center" data-testid="learn-article-not-found">
        <div className="text-center max-w-md">
          <h1 className="font-display text-2xl text-emerald-950">Article not found</h1>
          <p className="text-sm text-emerald-900/70 mt-2">We couldn't find that guide. It may have moved.</p>
          <button onClick={() => navigate("/learn")} className="mt-5 text-emerald-700 font-semibold hover:underline" data-testid="learn-back-to-index">
            ← Back to the wellness library
          </button>
        </div>
      </main>
    );
  }

  const readingMin = readingTimeOf(article.body_md, article.estimated_read_minutes);
  const lastUpdated = formatDate(article.updated_at || article.created_at);

  // Build the inline-visual list (social + pinterest) — hero is rendered
  // separately above the article. We pass these to the renderer which
  // distributes them at H2 boundaries.
  const inlineVisuals = (article.visual_assets || []).filter((v) => v.variant === "social" || v.variant === "pinterest");
  // Hero visual (for Pinterest "Save" button + share image)
  const heroVisual = (article.visual_assets || []).find((v) => v.variant === "hero");
  const pinterestVisual = (article.visual_assets || []).find((v) => v.variant === "pinterest");
  const pinShareImage = pinterestVisual?.url || heroVisual?.url || heroImage;

  // Build the auto Table of Contents from H2 lines in body_md
  const toc = (article.body_md || "")
    .split(/\r?\n/)
    .filter((l) => l.startsWith("## "))
    .map((l) => {
      const text = l.slice(3).trim();
      return { text, id: slugify(text) };
    });

  return (
    <main className="bg-leaf-pattern min-h-[calc(100vh-4rem)]" data-testid="learn-article-page">
      <ReadingProgress />

      {/* ─── Premium hero ──────────────────────────────────────────── */}
      <header className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12">
        <Link
          to="/learn"
          className="inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:text-emerald-800 font-semibold mb-6"
          data-testid="learn-article-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Wellness library
        </Link>

        <div className="grid lg:grid-cols-[1fr_auto] gap-8 lg:gap-12 items-start">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-semibold flex items-center gap-1.5">
              <BookOpen className="w-3.5 h-3.5" /> {article.condition_key?.replace(/_/g, " ") || (article.tags || [])[0] || "wellness guide"}
            </div>
            <h1 className="font-display text-3xl sm:text-5xl text-emerald-950 font-light tracking-tight mt-3 leading-[1.1]" data-testid="learn-article-title">
              {article.title}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-emerald-900/85 leading-relaxed max-w-2xl" data-testid="learn-article-hero-excerpt">
              {article.hero_excerpt}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-emerald-900/65">
              <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {readingMin} min read</span>
              {lastUpdated && <span className="inline-flex items-center gap-1.5"><span className="text-emerald-700/40">·</span> Updated {lastUpdated}</span>}
              <span className="inline-flex items-center gap-1.5"><span className="text-emerald-700/40">·</span> Feel Greats Editorial</span>
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-800 font-semibold text-[10.5px] uppercase tracking-wider">
                <Sparkles className="w-3 h-3" /> Wellness, not medical advice
              </span>
            </div>
          </div>

          {/* Hero image (Cloudinary CDN, 16:9, lazy fade-in) */}
          {heroImage && !heroImage.endsWith("/og-default.jpg") && (
            <figure className="relative w-full lg:w-[420px] xl:w-[480px] rounded-3xl overflow-hidden border border-emerald-100 shadow-sm group" data-testid="learn-article-hero-image">
              <div className="aspect-[16/9] sm:aspect-[4/5] lg:aspect-[4/5] bg-emerald-50/30 overflow-hidden">
                <img
                  src={heroImage}
                  alt={article.title}
                  loading="eager"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-[1.03]"
                />
              </div>
              {/* Pinterest "Save" hover button — Part 2 will swap href to live OAuth flow. */}
              {pinShareImage && (
                <a
                  href={`https://www.pinterest.com/pin/create/button/?url=${encodeURIComponent(`${PROD_DOMAIN}/learn/${article.slug}`)}&media=${encodeURIComponent(pinShareImage)}&description=${encodeURIComponent(article.meta_description || article.hero_excerpt || article.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute top-3 right-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur text-[11px] font-semibold text-emerald-900 shadow-sm border border-emerald-100 hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid="learn-article-pinterest-save"
                >
                  Save on Pinterest
                </a>
              )}
            </figure>
          )}
        </div>
      </header>

      {/* ─── Article body ───────────────────────────────────────────── */}
      <article className="max-w-6xl mx-auto px-4 sm:px-6 mt-10 sm:mt-12 pb-16">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_280px] gap-10 lg:gap-14">
          {/* ── Main column ───────────────────────────────────────── */}
          <div className="min-w-0 article-body">
            {/* AI-Overview-friendly enhancements: Quick Answer, Key Takeaways,
                Related Topics, Reviewed/Updated. Renders nothing when there
                is no editorial data to surface (graceful degradation). */}
            <ArticleAiEnhancements article={article} />

            <EditorialMarkdownRenderer body_md={article.body_md} visualEmbeds={inlineVisuals} />

            {/* Premium internal-link cards (after the body, before FAQ).
                Sourced from related[] (curated) + phasec_internal_links
                (AI-suggested). Capped at 4 so the section stays calm. */}
            <PremiumInternalLinks article={article} />

            {/* Share row (Phase C-9 distribution channels) */}
            <ArticleShareRow article={article} pinImage={pinShareImage} />

            {/* FAQ accordion + FAQPage schema (visible on page) */}
            {Array.isArray(article.faq) && article.faq.length > 0 ? (
              <section
                id="faq"
                className="mt-12 rounded-3xl border border-emerald-100 bg-white px-5 sm:px-7 py-6 sm:py-7"
                aria-labelledby="faq-heading"
                data-testid="learn-article-faq"
              >
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-4">
                  <BookOpen className="w-3.5 h-3.5" /> Common questions
                </div>
                <h2 id="faq-heading" className="font-display text-2xl sm:text-3xl text-emerald-950 leading-snug mb-5 font-light">
                  People also ask
                </h2>
                <ul className="divide-y divide-emerald-100/80">
                  {article.faq.map((f, i) => {
                    // Support BOTH `{q, a}` (legacy) and `{question, answer}` (BabyLoveGrowth import schema).
                    const question = f?.q || f?.question || "";
                    const answer = f?.a || f?.answer || "";
                    if (!question || !answer) return null;
                    return (
                      <li key={i} className="py-3">
                        <details className="group" data-testid={`learn-article-faq-item-${i}`}>
                          <summary className="cursor-pointer list-none flex items-start justify-between gap-3 font-medium text-emerald-950 hover:text-emerald-700 transition-colors">
                            <span className="leading-snug text-[15px] sm:text-base">{question}</span>
                            <span
                              aria-hidden
                              className="shrink-0 mt-0.5 text-emerald-700/60 group-open:rotate-45 transition-transform text-xl leading-none w-5 text-center"
                            >+</span>
                          </summary>
                          <p className="mt-2.5 text-[14.5px] leading-relaxed text-emerald-900/85">
                            {answer}
                          </p>
                        </details>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ) : null}

            {/* Related curated articles */}
            {Array.isArray(article.related) && article.related.length > 0 ? (
              <section className="mt-12" data-testid="learn-article-related">
                <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-4">
                  Continue reading
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {article.related.slice(0, 4).map((r) => (
                    <InternalLinkCard
                      key={r.slug}
                      slug={r.slug}
                      title={r.title}
                      hero_excerpt={r.hero_excerpt}
                    />
                  ))}
                </div>
              </section>
            ) : null}

            {/* CTA */}
            <Card className="rounded-3xl border-amber-200 bg-amber-50/60 mt-12">
              <CardContent className="p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
                    <ShieldCheck className="w-3.5 h-3.5" /> Ready to act on this?
                  </div>
                  <h3 className="font-display text-xl sm:text-2xl text-emerald-950 mt-2 font-light">
                    Take the 3-minute assessment.
                  </h3>
                  <p className="text-sm sm:text-[15px] text-emerald-900/75 mt-1.5 max-w-md leading-relaxed">
                    We'll build a personalised plan and recommend exactly the right products — no guesswork, no upselling.
                  </p>
                </div>
                <Link to="/register" data-testid="learn-article-cta-assessment">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-3 text-sm font-semibold transition-colors whitespace-nowrap">
                    Start now <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </CardContent>
            </Card>

            <p className="mt-10 text-xs text-emerald-900/55 leading-relaxed max-w-2xl">
              <strong>Wellness, not medical advice.</strong> This article is for educational purposes
              only and is not a substitute for professional medical advice, diagnosis or treatment.
              Always consult your GP or qualified healthcare professional before starting any new regimen.
            </p>
          </div>

          {/* ── Sidebar: Table of Contents (desktop only) ──────────── */}
          {toc.length >= 2 && (
            <aside className="hidden lg:block sticky top-24 self-start" data-testid="learn-article-toc">
              <div className="rounded-2xl border border-emerald-100 bg-white/70 backdrop-blur-sm p-4">
                <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-3">In this article</p>
                <nav>
                  <ol className="space-y-1.5 text-[13px] leading-snug">
                    {toc.map((h, i) => (
                      <li key={h.id}>
                        <a
                          href={`#${h.id}`}
                          className="group flex items-start gap-1.5 text-emerald-900/75 hover:text-emerald-900 transition-colors"
                          data-testid={`learn-article-toc-link-${i}`}
                        >
                          <ChevronRight className="w-3 h-3 mt-1 text-emerald-700/40 group-hover:text-emerald-700 transition-colors shrink-0" />
                          <span>{h.text}</span>
                        </a>
                      </li>
                    ))}
                  </ol>
                </nav>
              </div>
            </aside>
          )}
        </div>
      </article>
    </main>
  );
}


/* ───────────────────────────────────────────────────────────────────
   Premium internal-link cards block — uses Phase C-1 AI-detected
   internal links (`phasec_internal_links`) when available, falls back
   to `related[]` curated links.
─────────────────────────────────────────────────────────────────── */
function PremiumInternalLinks({ article }) {
  const ai = Array.isArray(article.phasec_internal_links) ? article.phasec_internal_links : [];
  if (ai.length === 0) return null;
  // De-dupe by target slug
  const seen = new Set();
  const links = ai
    .map((l) => {
      const url = l.target_url || l.url || "";
      const m = /\/learn\/([^/?#]+)/.exec(url);
      const slug = m ? m[1] : "";
      return slug ? { slug, anchor: l.anchor || l.text, hero_excerpt: l.summary || "" } : null;
    })
    .filter(Boolean)
    .filter((l) => {
      if (seen.has(l.slug)) return false;
      seen.add(l.slug);
      return true;
    })
    .slice(0, 4);
  if (links.length === 0) return null;
  return (
    <section className="mt-12" data-testid="learn-article-internal-links">
      <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-4">
        Read alongside
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {links.map((l) => (
          <InternalLinkCard
            key={l.slug}
            slug={l.slug}
            anchor={l.anchor}
            hero_excerpt={l.hero_excerpt}
          />
        ))}
      </div>
    </section>
  );
}
